/**
 * Rich text: a light inline-markdown dialect stored straight in `node.text`.
 *
 * There is no schema change and no separate CRDT type for formatted text —
 * `**bold**`, `*italic*`, `~~strike~~`, `[label](url)` links and `- ` bullet
 * lines live in the same plain string every node already had. That keeps
 * editing a plain `<textarea>` (so IME, spellcheck and undo all keep working
 * for free) while the canvas — and export — render the markup instead of the
 * raw asterisks.
 *
 * The trade-off: formatting is per-run, not per-character-with-a-cursor, and
 * an unmatched `*` just toggles italics until the next one or end of line.
 * For sticky notes and short labels that is worth it in exchange for zero new
 * moving parts in the collaboration layer.
 */
import { FONT_STACKS } from './palette.js';
import type { Measure } from './textLayout.js';
import type { TextStyle } from './types.js';

export interface RichRun {
  text: string;
  bold: boolean;
  italic: boolean;
  strike: boolean;
  /** Present when this run is a `[label](url)` link. */
  link: string | null;
}

interface RichParagraph {
  bullet: boolean;
  runs: RichRun[];
}

/** Left indent (in font-size units) a bulleted paragraph's lines get. */
export const BULLET_INDENT_EM = 1.3;
const BULLET_GLYPH = '•';

function isMarkerAt(line: string, i: number): boolean {
  const c = line[i];
  return c === '*' || c === '~' || c === '[';
}

/** Inline markup within one paragraph (no newlines). */
function parseInline(line: string): RichRun[] {
  const runs: RichRun[] = [];
  let bold = false;
  let italic = false;
  let strike = false;
  let i = 0;

  while (i < line.length) {
    if (line.startsWith('**', i)) {
      bold = !bold;
      i += 2;
      continue;
    }
    if (line.startsWith('~~', i)) {
      strike = !strike;
      i += 2;
      continue;
    }
    if (line[i] === '*') {
      italic = !italic;
      i += 1;
      continue;
    }
    if (line[i] === '[') {
      const closeBracket = line.indexOf(']', i + 1);
      if (closeBracket !== -1 && line[closeBracket + 1] === '(') {
        const closeParen = line.indexOf(')', closeBracket + 2);
        if (closeParen !== -1) {
          const label = line.slice(i + 1, closeBracket);
          const url = line.slice(closeBracket + 2, closeParen);
          if (label !== '') runs.push({ text: label, bold, italic, strike, link: url });
          i = closeParen + 1;
          continue;
        }
      }
    }

    let j = i + 1;
    while (j < line.length && !isMarkerAt(line, j)) j++;
    runs.push({ text: line.slice(i, j), bold, italic, strike, link: null });
    i = j;
  }

  return runs;
}

export function parseRichText(text: string): RichParagraph[] {
  return text.split('\n').map((line) => {
    const bullet = line.startsWith('- ') || line.startsWith('* ');
    const body = bullet ? line.slice(2) : line;
    return { bullet, runs: parseInline(body) };
  });
}

/** Plain-text projection, markup stripped — used for search and export text. */
export function stripRichMarkup(text: string): string {
  return parseRichText(text)
    .map((p) => (p.bullet ? '• ' : '') + p.runs.map((r) => r.text).join(''))
    .join('\n');
}

export function richFontString(style: TextStyle, run: { bold: boolean; italic: boolean }, sizeOverride?: number): string {
  const size = sizeOverride ?? style.fontSize;
  const weight = run.bold ? '600' : '400';
  const italic = run.italic ? 'italic ' : '';
  const family = FONT_STACKS[style.fontFamily] ?? FONT_STACKS.sans;
  return `${italic}${weight} ${size}px ${family}`;
}

interface RichWord {
  text: string;
  bold: boolean;
  italic: boolean;
  strike: boolean;
  link: string | null;
  width: number;
}

export interface RichLine {
  words: RichWord[];
  bullet: boolean;
  width: number;
}

export interface RichTextLayout {
  lines: RichLine[];
  fontSize: number;
  lineHeight: number;
  width: number;
  height: number;
}

function wordsOf(runs: RichRun[], font: (bold: boolean, italic: boolean) => string, measure: Measure): RichWord[] {
  const out: RichWord[] = [];
  for (const run of runs) {
    for (const token of run.text.split(/(\s+)/)) {
      if (token === '') continue;
      out.push({
        text: token,
        bold: run.bold,
        italic: run.italic,
        strike: run.strike,
        link: run.link,
        width: measure(token, font(run.bold, run.italic)),
      });
    }
  }
  return out;
}

export function layoutRichText(
  text: string,
  style: TextStyle,
  maxWidth: number,
  measure: Measure,
  fontSizeOverride?: number,
): RichTextLayout {
  const fontSize = fontSizeOverride ?? style.fontSize;
  const font = (bold: boolean, italic: boolean) => richFontString(style, { bold, italic }, fontSize);
  const lineHeight = fontSize * style.lineHeight;
  const bulletIndent = BULLET_INDENT_EM * fontSize;

  const lines: RichLine[] = [];

  for (const paragraph of parseRichText(text)) {
    const available = Math.max(maxWidth - (paragraph.bullet ? bulletIndent : 0), 1);
    const words = wordsOf(paragraph.runs, font, measure);

    if (words.length === 0) {
      lines.push({ words: [], bullet: paragraph.bullet, width: 0 });
      continue;
    }

    let current: RichWord[] = [];
    let currentWidth = 0;
    const flush = () => {
      lines.push({ words: current, bullet: paragraph.bullet, width: currentWidth });
      current = [];
      currentWidth = 0;
    };

    for (const word of words) {
      const nextWidth = currentWidth + word.width;
      if (nextWidth <= available || current.length === 0) {
        if (nextWidth > available && current.length === 0 && word.text.trim() !== '') {
          // A single token wider than the line (long URL, long word): let it
          // overflow rather than trying to split formatting mid-run.
          current.push(word);
          currentWidth = word.width;
          continue;
        }
        current.push(word);
        currentWidth = nextWidth;
      } else {
        flush();
        if (word.text.trim() === '') continue; // drop leading space after a wrap
        current.push(word);
        currentWidth = word.width;
      }
    }
    flush();
  }

  let width = 0;
  for (const l of lines) {
    const w = l.width + (l.bullet ? bulletIndent : 0);
    if (w > width) width = w;
  }

  return {
    lines,
    fontSize,
    lineHeight,
    width,
    height: Math.max(lines.length, 1) * lineHeight,
  };
}

export function fitRichTextToBox(
  text: string,
  style: TextStyle,
  box: { w: number; h: number },
  measure: Measure,
  minFontSize = 8,
): RichTextLayout {
  const initial = layoutRichText(text, style, box.w, measure);
  if (text === '' || initial.height <= box.h) return initial;

  let lo = minFontSize;
  let hi = style.fontSize;
  let best = layoutRichText(text, style, box.w, measure, lo);

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const attempt = layoutRichText(text, style, box.w, measure, mid);
    if (attempt.height <= box.h) {
      best = attempt;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

export { BULLET_GLYPH };
